import React, { FC, useContext, useCallback } from 'react';
import { SEVERITIES_INFO } from '../../../../consts/Severity';
import { RISK_SEVERITY } from '@silverfort-nodelibs/common/utils/insight';
import ItdrContext from '../../ItdrContext';
import { LAST_CHANGES_FILTERS } from '../Filters';
import { DonutChart, HexColor, LayoutCarousel, SEVERITIES_COLORS } from '@silverfort/sf-components';
import { addOrRemoveIfPresentFromArray } from '../../../../utils/utils';
import useGetIncidentsSeverityOverview from '../../api/queries/useGetIncidentsSeverityOverview';

const formatNumber = (num: number): string => {
    if (num < 1000) return num.toString();

    const formatter = new Intl.NumberFormat('en', {
        notation: 'compact',
        maximumSignificantDigits: 3
    });

    return formatter.format(num);
};

const IncidentsBySeverity: FC = () => {
    const { lastActivityDiffInDays, setSelectedSeverities } = useContext(ItdrContext);
    const { isFetching, data: severitiesOverview } = useGetIncidentsSeverityOverview({
        lastActivityFilterInDays: lastActivityDiffInDays ?? LAST_CHANGES_FILTERS.LAST_DAY
    });

    const onItemSelected = useCallback(
        (severityValue: number) => {
            setSelectedSeverities((prev) => addOrRemoveIfPresentFromArray(severityValue, prev));
        },
        [setSelectedSeverities]
    );

    const data = Object.values(RISK_SEVERITY)
        .filter((severity) => severity !== RISK_SEVERITY.SECURE)
        .map((severity) => ({
            name: SEVERITIES_INFO[severity].label,
            value: severitiesOverview?.[SEVERITIES_INFO[severity].label.toLowerCase()] ?? 0,
            fill: SEVERITIES_COLORS[severity].hex as HexColor,
            onClick: () => onItemSelected(SEVERITIES_INFO[severity].value)
        }))
        .sort((a, b) => b.value - a.value);

    const total = data.reduce((sum, item) => sum + item.value, 0);

    return (
        <LayoutCarousel
            pages={
                <DonutChart
                    chartSize={120}
                    data={data}
                    innerRadius={50}
                    isLoading={isFetching}
                    isMappingVisible
                    outerRadius={60}
                    totalText={formatNumber(total)}
                />
            }
            title="Active Incidents by Severity"
        />
    );
};

export default IncidentsBySeverity;
